import { Card } from '@lib/ui';

type SourcePreviewProps = {
  title: string;
  data: unknown;
  format: 'json' | 'yaml';
};

function toYaml(obj: unknown, indent = 0): string {
  const spaces = '  '.repeat(indent);

  if (obj === null || obj === undefined) {
    return 'null';
  }

  if (typeof obj === 'string') {
    // Multi-line strings
    if (obj.includes('\n')) {
      const lines = obj.split('\n');
      return `|\n${lines.map((line) => spaces + '  ' + line).join('\n')}`;
    }
    // Strings with special characters
    if (obj.includes(':') || obj.includes('#') || obj.includes('{') || obj.includes('}')) {
      return `"${obj.replace(/"/g, '\\"')}"`;
    }
    return obj || '""';
  }

  if (typeof obj === 'number' || typeof obj === 'boolean') {
    return String(obj);
  }

  if (Array.isArray(obj)) {
    if (obj.length === 0) return '[]';
    return obj
      .map((item) => {
        const value = toYaml(item, indent + 1);
        if (typeof item === 'object' && item !== null) {
          // For objects in arrays, put first key on same line as dash
          const lines = value.split('\n');
          return `${spaces}- ${lines[0].trim()}\n${lines.slice(1).join('\n')}`;
        }
        return `${spaces}- ${value}`;
      })
      .join('\n');
  }

  if (typeof obj === 'object') {
    const entries = Object.entries(obj as Record<string, unknown>);
    if (entries.length === 0) return '{}';
    return entries
      .filter(([, value]) => {
        // Skip empty arrays, empty objects, empty strings, null, undefined
        if (value === null || value === undefined || value === '') return false;
        if (Array.isArray(value) && value.length === 0) return false;
        if (typeof value === 'object' && !Array.isArray(value) && Object.keys(value).length === 0)
          return false;
        return true;
      })
      .map(([key, value]) => {
        const valueStr = toYaml(value, indent + 1);
        if (
          typeof value === 'object' &&
          value !== null &&
          (Array.isArray(value) || Object.keys(value).length > 0)
        ) {
          return `${spaces}${key}:\n${valueStr}`;
        }
        return `${spaces}${key}: ${valueStr}`;
      })
      .join('\n');
  }

  return String(obj);
}

export function SourcePreview({ title, data, format }: SourcePreviewProps) {
  const content = format === 'json' ? JSON.stringify(data, null, 2) : toYaml(data);

  return (
    <Card skin="shadow" className="p-4">
      <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">{title}</h3>
      <pre className="p-4 rounded-lg bg-gray-900 dark:bg-dark-950 text-sm text-gray-100 overflow-x-auto">
        <code>{content}</code>
      </pre>
    </Card>
  );
}
